<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { UserService } from '@/services/user-service'
import { mdiAccount, mdiLock, mdiEye, mdiEyeOff } from '@mdi/js'
import { Notify } from '@/modules/util/notify'
import { Settings } from '@/settings'
import { Cache } from '@/modules/cache/main'

const userService = new UserService
const router = useRouter()

const valid = ref(false)
const username = ref('')
const password = ref('')
const show = ref(false)
const get2fa = ref(false)
const input2fa = ref('')
const showClearCache = ref(false)
const clearInProgress = ref(false)

const rules = {
	required: val => !!val || 'Required',
}

const tryLogin = async () => {
	if (get2fa.value) {
		return await try2FALogin()
	}

	try {
		const res = await userService.post('/login', {
			username: username.value,
			password: password.value,
		})

		if (res.body.status && res.body.requires_2fa) {
			get2fa.value = true
			return
		}
        
		if (!(res.body?.status && res.body?.usertoken)) {
			Notify.error(res.body.errorType === 'AUTHENTICATION_FAILED' 
				? res.body.errorMessage 
				: 'An error occurred. Please try again.'
			)
			return
		}
        
		localStorage.setItem('username', username.value)
		localStorage.setItem('usertoken', res.body.usertoken)

		await Settings.init()
        
		router.push({ name: 'list-vehicles' })
	} catch (err) {
		Notify.error('An error occurred. Please try again.')
	}
}

const try2FALogin = async () => {
	try {
		const res = await userService.post('/login', { 
			username: username.value,
			password: password.value,
			user2fa_code: input2fa.value 
		})
		
		if (!(res.body?.status && res.body?.usertoken)) {
			Notify.error(res.body.errorType === 'AUTHENTICATION_FAILED' 
				? res.body.errorMessage 
				: 'An error occurred. Please try again.'
			)
			return
		}

		localStorage.setItem('username', username.value)
		localStorage.setItem('usertoken', res.body.usertoken)

		await Settings.init()

		router.push({ name: 'list-vehicles' })
	} catch (err) {
		Notify.error('An error occurred. Please try again.')
	}
}

const clearCache = async () => {
	clearInProgress.value = true
	localStorage.clear()
	await Cache.clearAll()
	await Settings.init()
	clearInProgress.value = false
	showClearCache.value = false
}
</script>

<template>
<v-form
	v-model="valid"
	class="mt-16"
	@submit.prevent="tryLogin"
>
	<v-card
		class="mx-auto"
		max-width="344"
		:title="get2fa ? 'Enter 2FA Code' : 'Sign In'"
	>
		<v-container v-if="!get2fa">
			<v-text-field
				v-model="username"
				label="Username"
				variant="underlined"
				color="primary"
				type="email"
				:prepend-icon="mdiAccount"
				:rules="[rules.required]"
			/>
			<v-text-field
				v-model="password"
				label="Password"
				variant="underlined"
				color="primary"
				:type="show ? 'text' : 'password'"
				:prepend-icon="mdiLock"
				:append-inner-icon="show ? mdiEyeOff : mdiEye"
				:rules="[rules.required]"
				@click:append-inner="show = !show"
			/>
			<v-btn
				class="w-100 mt-4"
				type="submit"
				color="primary"
				:disabled="!valid"
			>
				Sign In
			</v-btn>
		</v-container>
		<v-container v-else>
			<v-otp-input v-model="input2fa" />
			<v-btn
				class="w-100 mt-4"
				type="submit"
				color="primary"
				:disabled="input2fa.length < 5"
			>
				Submit
			</v-btn>
		</v-container>
	</v-card>
</v-form>

<v-card
	class="mx-auto my-4"
	max-width="344"
>
	<v-expansion-panels>
		<v-expansion-panel
			title="Actions"
		>
			<v-expansion-panel-text>
				<v-btn
					color="error"
					@click="showClearCache = true"
				>
					Clear Cache
				</v-btn>
			</v-expansion-panel-text>
		</v-expansion-panel>
	</v-expansion-panels>
</v-card>

<v-dialog v-model="showClearCache">
	<v-card title="Are you sure?"> 
		<v-card-text>This action cannot be undone. Any cached inspections which were waiting to upload will be deleted, alongside any image data and progress on incomplete inspections.</v-card-text>
		<v-card-actions>
			<v-btn @click="showClearCache = false">
				Cancel
			</v-btn>
			<v-btn
				color="error"
				:loading="clearInProgress"
				@click="clearCache"
			>
				Clear Cache
			</v-btn>
		</v-card-actions>
	</v-card>
</v-dialog>
</template>

<style lang="scss" scoped>

</style>